import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Textarea,
} from '@bookaburra/ui';
import { AlertTriangle, CheckCircle2, RotateCcw, XCircle } from 'lucide-react';
import {
  commitReshuffle,
  planReshuffle,
  type ReshuffleRow,
} from '../booking-flow/reshuffle-api';
import { fetchAdminCalendar } from '../booking-flow/admin-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';
import { PlanGate } from '../components/PlanGate';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const Route = createFileRoute('/admin/$slug/reshuffle')({
  component: ReshufflePage,
});

function toLocalDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ReshufflePage() {
  const { slug } = Route.useParams();
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Reshuffle · ${tenantQ.data.tenant.name} Admin` : 'Reshuffle',
  );

  // Default to "all day today" for the sick window
  const now = useMemo(() => new Date(), []);
  const todayStart = useMemo(() => {
    const d = new Date(now);
    d.setHours(8, 0, 0, 0);
    return d;
  }, [now]);
  const todayEnd = useMemo(() => {
    const d = new Date(now);
    d.setHours(17, 0, 0, 0);
    return d;
  }, [now]);

  const [staffId, setStaffId] = useState<string>('');
  const [rangeStart, setRangeStart] = useState<string>(toLocalDateTime(todayStart));
  const [rangeEnd, setRangeEnd] = useState<string>(toLocalDateTime(todayEnd));
  const [note, setNote] = useState('');
  const [plan, setPlan] = useState<ReshuffleRow[] | null>(null);
  const [planRange, setPlanRange] = useState<{ start: number; end: number } | null>(null);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [commitResult, setCommitResult] = useState<
    null | { applied: { bookingId: string; action: string }[] }
  >(null);

  // Staff list comes from the calendar endpoint we already have
  const staff = useQuery({
    queryKey: ['admin-staff', slug],
    queryFn: () =>
      fetchAdminCalendar(slug, Date.now(), Date.now() + 7 * MS_PER_DAY).then(
        (cal) => cal.staff,
      ),
  });

  const planMut = useMutation({
    mutationFn: () => {
      const start = new Date(rangeStart).getTime();
      const end = new Date(rangeEnd).getTime();
      return planReshuffle(slug, { staffId, rangeStart: start, rangeEnd: end });
    },
    onSuccess: (r) => {
      setPlan(r.plan.rows);
      setPlanRange(r.plan.range);
      // Pre-select all auto-reassignable rows
      setAccepted(
        new Set(
          r.plan.rows
            .filter((row) => row.proposed.kind === 'reassign')
            .map((row) => row.bookingId),
        ),
      );
      setCommitResult(null);
    },
  });

  const commitMut = useMutation({
    mutationFn: () => {
      if (!planRange) throw new Error('no plan');
      return commitReshuffle(slug, {
        staffId,
        rangeStart: planRange.start,
        rangeEnd: planRange.end,
        note: note.trim() || undefined,
        acceptedBookingIds: [...accepted],
      });
    },
    onSuccess: (r) => setCommitResult({ applied: r.applied }),
  });

  function toggleAccept(bookingId: string) {
    setAccepted((s) => {
      const next = new Set(s);
      if (next.has(bookingId)) next.delete(bookingId);
      else next.add(bookingId);
      return next;
    });
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-10 sm:px-12">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">Sick-day reshuffle</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Mark a staff member unavailable for a window. We&rsquo;ll suggest a
          plan for the affected bookings. Clients are only notified after you
          commit.
        </p>
      </header>

      <PlanGate slug={slug} requiredPlan="large-business">
      <Card className="p-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            planMut.mutate();
          }}
          className="grid gap-4 sm:grid-cols-2"
        >
          <FormField label="Staff member" required>
            {({ id }) => (
              <select
                id={id}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                required
              >
                <option value="" disabled>Choose…</option>
                {staff.data?.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </FormField>
          <FormField label="Note (private)" description="e.g. flu, family emergency">
            {({ id }) => (
              <Input id={id} value={note} onChange={(e) => setNote(e.target.value)} />
            )}
          </FormField>
          <FormField label="From" required>
            {({ id }) => (
              <Input
                id={id}
                type="datetime-local"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label="To" required>
            {({ id }) => (
              <Input
                id={id}
                type="datetime-local"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                required
              />
            )}
          </FormField>

          <div className="sm:col-span-2 flex justify-end">
            <Button
              type="submit"
              loading={planMut.isPending}
              disabled={!staffId || !rangeStart || !rangeEnd}
              leadingIcon={<RotateCcw className="h-4 w-4" />}
            >
              Run plan
            </Button>
          </div>
        </form>
        {planMut.error ? (
          <Alert variant="destructive" className="mt-4">
            <XCircle className="h-4 w-4" />
            <AlertTitle>Couldn&rsquo;t plan</AlertTitle>
            <AlertDescription>{(planMut.error as Error).message}</AlertDescription>
          </Alert>
        ) : null}
      </Card>

      {plan ? (
        <section className="mt-10">
          <header className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Proposed plan</h2>
              <p className="text-sm text-muted-foreground">
                {plan.length === 0
                  ? 'No bookings overlap this window.'
                  : `${plan.length} booking${plan.length === 1 ? '' : 's'} affected · ${accepted.size} selected to apply`}
              </p>
            </div>
            {plan.length > 0 ? (
              <Button
                onClick={() => commitMut.mutate()}
                loading={commitMut.isPending}
                disabled={accepted.size === 0}
              >
                Commit {accepted.size} {accepted.size === 1 ? 'change' : 'changes'}
              </Button>
            ) : null}
          </header>

          {commitResult ? (
            <Alert variant="success" className="mt-4">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Reshuffle committed</AlertTitle>
              <AlertDescription>
                {commitResult.applied.filter((a) => a.action === 'reassigned').length} reassigned,{' '}
                {commitResult.applied.filter((a) => a.action === 'skipped').length} skipped.
                Clients have been notified.
              </AlertDescription>
            </Alert>
          ) : null}

          <ul className="mt-4 space-y-2">
            {plan.map((row) => (
              <PlanRow
                key={row.bookingId}
                row={row}
                accepted={accepted.has(row.bookingId)}
                onToggle={() => toggleAccept(row.bookingId)}
                disabled={Boolean(commitResult)}
              />
            ))}
          </ul>
        </section>
      ) : null}
      </PlanGate>
    </main>
  );
}

function PlanRow({
  row,
  accepted,
  onToggle,
  disabled,
}: {
  row: ReshuffleRow;
  accepted: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  const time = `${new Date(row.current.range.start).toLocaleDateString(
    'en-AU',
    { dateStyle: 'medium' },
  )} ${new Date(row.current.range.start).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`;
  const isManual = row.proposed.kind === 'manual';

  return (
    <li className="rounded-lg border border-border bg-card px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold">{row.serviceName}</p>
          <p className="text-sm text-muted-foreground">
            {time}
            {row.clientName ? ` · ${row.clientName}` : null}
          </p>
        </div>
        {isManual ? (
          <Badge variant="warning">Manual triage</Badge>
        ) : (
          <Badge variant="success">Auto reassign</Badge>
        )}
      </div>

      {isManual ? (
        <Alert variant="warning" className="mt-3">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No automatic match</AlertTitle>
          <AlertDescription>
            Reason: <code>{row.proposed.kind === 'manual' ? row.proposed.reason : ''}</code>.
            Phone the client to reschedule.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="mt-3 flex items-start gap-3">
          <input
            type="checkbox"
            checked={accepted}
            onChange={onToggle}
            disabled={disabled}
            aria-label={`Accept reassignment for ${row.serviceName}`}
            className="mt-1 h-4 w-4"
          />
          <p className="text-sm text-muted-foreground">
            Same time, different staff. {accepted ? 'Will be applied.' : 'Tick to apply.'}
          </p>
        </div>
      )}
    </li>
  );
}
